import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Avatar, useTheme } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { PlatformUserAvatar } from 'src/components/PlatformUserAvatar/platform-user-avatar';
import { SearchDialog, SearchDialogItem } from 'src/components/SearchDialog/search-dialog';
import { useConversationParticipantOptions } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { validateEmail } from '..';

const ADD_EMAIL_ID = 'add-email';

interface ParticipantSearchItem extends SearchDialogItem {
  externalPartnerId?: number;
  participantEntity?: ConversationParticipantEntity;
  entityPartnerId?: number;
  avatarFileKey?: string | null;
  handler: () => void;
}

export const UserSearchModal: React.FC = () => {
  const theme = useTheme();
  const { modalProps } = useModalState(Modals.UserSearchModal);
  const { closeModal } = useModalMethods(Modals.UserSearchModal);

  const { partnerId, onSelect } = modalProps!;

  const [searchValue, setSearchValue] = useState('');

  const { getConversationParticipantOptions, conversationParticipantOptions, loading } =
    useConversationParticipantOptions();

  useEffect(() => {
    getConversationParticipantOptions(
      {
        partnerId,
        searchText: searchValue,
      },
      true,
    );
  }, [partnerId, searchValue, getConversationParticipantOptions]);

  const filteredOptions = useMemo(
    () =>
      conversationParticipantOptions
        .filter(
          (opt) => opt.email && opt.participantEntity !== ConversationParticipantEntity.Rsm,
        )
        .map((participant) => {
          const externalPartnerId =
            participant.entityPartnerId !== partnerId
              ? participant.entityPartnerId
              : undefined;
          return {
            id: participant.id,
            title: isNullish(externalPartnerId)
              ? participant.label
              : `Workspace: ${externalPartnerId} ${participant.label}`,
            description: participant.email,
            group: participant.group,
            externalPartnerId,
            participantEntity: participant.participantEntity,
            entityPartnerId: participant.entityPartnerId,
            avatarFileKey: participant.avatarFileKey,
            handler: () => {
              onSelect(participant);
              closeModal();
            },
          };
        }),
    [closeModal, conversationParticipantOptions, onSelect, partnerId],
  );

  const options = useMemo(
    () =>
      filteredOptions.length === 0 && searchValue && validateEmail(searchValue)
        ? [
            {
              id: ADD_EMAIL_ID,
              title: `Add ${searchValue}`,
              icon: (
                <Avatar sx={{ bgcolor: theme.palette.primary.main, marginRight: '1rem' }}>
                  <MailOutlineIcon />
                </Avatar>
              ),
              handler: () => {
                onSelect(searchValue);
                closeModal();
              },
            },
          ]
        : filteredOptions,
    [closeModal, filteredOptions, onSelect, searchValue, theme.palette.primary.main],
  );

  const onItemSelect = useCallback(
    (item: ParticipantSearchItem) => {
      closeModal();
      item.handler?.();
    },
    [closeModal],
  );

  const renderItemStartIcon = useCallback((item: ParticipantSearchItem) => {
    if (item.participantEntity === ConversationParticipantEntity.PlatformUser) {
      return (
        <PlatformUserAvatar
          partnerId={item.entityPartnerId}
          avatarFileKey={item.avatarFileKey}
          name={item.title}
          sx={{ marginRight: '1rem' }}
        />
      );
    }
    return <ColoredAvatar name={item.title} sx={{ marginRight: '1rem' }} />;
  }, []);

  const listItemStyles = useCallback((item: ParticipantSearchItem) => {
    return item.id === ADD_EMAIL_ID
      ? {
          '& .MuiListItemText-primary': {
            fontWeight: '400',
            fontSize: '1rem',
          },
        }
      : {};
  }, []);

  return (
    <SearchDialog
      loading={options.length === 0 && loading}
      showProgress={loading}
      options={options}
      onClose={closeModal}
      placeholder="Search people or type an email..."
      onSelect={onItemSelect}
      endAdornment={null}
      width="30rem"
      renderItemStartIcon={renderItemStartIcon}
      onSearchChange={setSearchValue}
      listItemStyles={listItemStyles}
      onSearchChangeDebounce={500}
    />
  );
};
